import { useEffect, useState } from "react";
import { useTenancyAuth } from "@/context/TenancyAuthContext";
import TenancyLayout from "@/components/tenancy/TenancyLayout";

type Tenant = {
  id: string;
  nombre_empresa: string;
  codigo: string;
  db_name: string;
  activo: boolean;
  created_at: string;
};

export default function TenancyDashboard() {
  const { tenancyToken } = useTenancyAuth();
  const API = import.meta.env.VITE_API_URL_BACKEND;
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ nombre_empresa: "", codigo: "" });
  const [creating, setCreating] = useState(false);
  const [seedModal, setSeedModal] = useState<Tenant | null>(null);
  const [seedForm, setSeedForm] = useState({
    correo_electronico: "",
    contrasena: "",
    nombres: "",
    apellidos: "",
    numero_identificacion: "",
  });
  const [search, setSearch] = useState("");

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/tenants`, {
        headers: { Authorization: `Bearer ${tenancyToken}` },
      });
      const data = await res.json();
      setTenants(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await fetch(`${API}/tenants`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tenancyToken}`,
        },
        body: JSON.stringify({
          nombre_empresa: form.nombre_empresa,
          codigo: form.codigo.toLowerCase(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Error");
      setForm({ nombre_empresa: "", codigo: "" });
      setShowCreate(false);
      fetchTenants();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleSync = async (codigo: string) => {
    await fetch(`${API}/tenants/${codigo}/sync`, {
      method: "POST",
      headers: { Authorization: `Bearer ${tenancyToken}` },
    });
    alert(`Sincronizado ${codigo}`);
  };

  const handleSeed = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!seedModal) return;
    const res = await fetch(`${API}/tenants/${seedModal.codigo}/seed-admin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tenancyToken}`,
      },
      body: JSON.stringify({
        numero_identificacion: seedForm.numero_identificacion,
        nombres: seedForm.nombres,
        apellidos: seedForm.apellidos,
        correo_electronico: seedForm.correo_electronico,
        contrasena: seedForm.contrasena,
        rol: 1,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.message || "Error creando admin");
      return;
    }
    alert(data.message || `Admin creado en ${seedModal.codigo}`);
    setSeedModal(null);
  };

  const filtered = tenants.filter(
    (t) =>
      t.nombre_empresa.toLowerCase().includes(search.toLowerCase()) ||
      t.codigo.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <TenancyLayout>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-[32px] font-[650] tracking-[-0.03em]">
              Empresas
            </h1>
            <p className="text-[14px] text-gray-500 mt-1">
              {tenants.length} bases de datos activas • Cada empresa = una BD
              física PostgreSQL
            </p>
          </div>
          <div className="flex items-center gap-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar empresa o código..."
              className="h-10 w-[240px] rounded-full border border-black/10 bg-white px-4 text-[13px] outline-none focus:border-black dark:bg-white/[0.06] dark:border-white/10"
            />
            <button
              onClick={() => setShowCreate(true)}
              className="h-10 rounded-full bg-black px-5 text-[13px] font-medium text-white hover:bg-zinc-800 dark:bg-white dark:text-black"
            >
              + Nueva empresa
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-[20px] border border-black/[0.06] bg-white p-5 dark:bg-zinc-900 dark:border-white/10">
            <p className="text-[11px] uppercase tracking-widest text-gray-400">
              Total
            </p>
            <p className="mt-2 text-[28px] font-semibold">{tenants.length}</p>
            <p className="text-[12px] text-gray-500">tenants registrados</p>
          </div>
          <div className="rounded-[20px] border border-black/[0.06] bg-white p-5 dark:bg-zinc-900 dark:border-white/10">
            <p className="text-[11px] uppercase tracking-widest text-gray-400">
              Activos
            </p>
            <p className="mt-2 text-[28px] font-semibold">
              {tenants.filter((t) => t.activo).length}
            </p>
            <p className="text-[12px] text-gray-500">en operación</p>
          </div>
          <div className="rounded-[20px] border border-black/[0.06] bg-white p-5 dark:bg-zinc-900 dark:border-white/10">
            <p className="text-[11px] uppercase tracking-widest text-gray-400">
              Master DB
            </p>
            <p className="mt-2 text-[16px] font-medium truncate">
              master_multifuncional
            </p>
            <p className="text-[12px] text-gray-500">singleton admin</p>
          </div>
        </div>

        <div className="overflow-hidden rounded-[24px] border border-black/[0.06] bg-white dark:bg-zinc-900 dark:border-white/10">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-black/[0.06] text-[11px] uppercase tracking-widest text-gray-400 dark:border-white/10">
                  <th className="px-6 py-4 font-medium">Empresa</th>
                  <th className="px-6 py-4 font-medium">Código / BD</th>
                  <th className="px-6 py-4 font-medium">Creada</th>
                  <th className="px-6 py-4 font-medium">Estado</th>
                  <th className="px-6 py-4 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-12 text-center text-sm text-gray-400"
                    >
                      Cargando empresas...
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-12 text-center text-sm text-gray-400"
                    >
                      No hay empresas
                    </td>
                  </tr>
                ) : (
                  filtered.map((t) => (
                    <tr
                      key={t.id}
                      className="border-b border-black/[0.04] last:border-0 hover:bg-[#fafaf8] dark:border-white/[0.04] dark:hover:bg-white/[0.02] transition"
                    >
                      <td className="px-6 py-4">
                        <p className="text-[14px] font-medium">
                          {t.nombre_empresa}
                        </p>
                        <p className="text-[11px] text-gray-500 font-mono">
                          {t.id.slice(0, 8)}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-[13px] font-mono font-medium">
                          {t.codigo}
                        </p>
                        <p className="text-[11px] text-gray-500 font-mono">
                          {t.db_name}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-[12px] text-gray-500">
                        {new Date(t.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex h-6 items-center rounded-full px-2.5 text-[11px] font-medium ${
                            t.activo
                              ? "bg-green-50 text-green-700 border border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900"
                              : "bg-red-50 text-red-700"
                          }`}
                        >
                          {t.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-1.5">
                          <button
                            onClick={() => setSeedModal(t)}
                            className="h-8 rounded-full border border-black/10 px-3 text-[11px] font-medium hover:bg-black hover:text-white transition dark:border-white/10"
                          >
                            Admin
                          </button>
                          <button
                            onClick={() => handleSync(t.codigo)}
                            className="h-8 rounded-full border border-black/10 px-3 text-[11px] font-medium hover:bg-black hover:text-white transition dark:border-white/10"
                          >
                            Sincronizar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-6">
          <form
            onSubmit={handleCreate}
            className="w-full max-w-[440px] rounded-[24px] bg-white p-7 shadow-2xl dark:bg-zinc-900"
          >
            <h3 className="text-[18px] font-semibold">Nueva empresa</h3>
            <p className="mt-1 text-[12px] text-gray-500">
              Se creará una base de datos física PostgreSQL
            </p>
            <div className="mt-6 space-y-4">
              <div>
                <label className="text-[11px] font-medium uppercase tracking-widest text-gray-500">
                  Nombre empresa
                </label>
                <input
                  value={form.nombre_empresa}
                  onChange={(e) =>
                    setForm({ ...form, nombre_empresa: e.target.value })
                  }
                  required
                  placeholder="Constructora Montería"
                  className="mt-2 w-full h-11 rounded-full border border-black/10 bg-[#f9f9f7] px-4 text-[14px] outline-none focus:border-black dark:bg-white/[0.06] dark:border-white/10"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium uppercase tracking-widest text-gray-500">
                  Código único (sin espacios)
                </label>
                <input
                  value={form.codigo}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      codigo: e.target.value.toLowerCase().replace(/\s/g, "_"),
                    })
                  }
                  required
                  placeholder="monteria"
                  className="mt-2 w-full h-11 rounded-full border border-black/10 bg-[#f9f9f7] px-4 text-[14px] font-mono outline-none focus:border-black dark:bg-white/[0.06] dark:border-white/10"
                />
                <p className="mt-1.5 text-[11px] text-gray-400">
                  BD: {form.codigo || "codigo"}_db
                </p>
              </div>
            </div>
            <div className="mt-7 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="h-10 rounded-full border border-black/10 px-5 text-[13px] dark:border-white/10"
              >
                Cancelar
              </button>
              <button
                disabled={creating}
                className="h-10 rounded-full bg-black px-6 text-[13px] font-medium text-white dark:bg-white dark:text-black"
              >
                {creating ? "Creando..." : "Crear BD y tenant"}
              </button>
            </div>
          </form>
        </div>
      )}

      {seedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-6">
          <form
            onSubmit={handleSeed}
            className="w-full max-w-[460px] rounded-[24px] bg-white p-7 shadow-2xl dark:bg-zinc-900"
          >
            <h3 className="text-[18px] font-semibold">
              Crear admin en {seedModal.codigo}
            </h3>
            <p className="mt-1 text-[12px] text-gray-500">
              Primer usuario superadmin de esa empresa
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <input
                value={seedForm.numero_identificacion}
                onChange={(e) =>
                  setSeedForm({
                    ...seedForm,
                    numero_identificacion: e.target.value,
                  })
                }
                required
                placeholder="Cédula"
                className="h-11 rounded-full border border-black/10 bg-[#f9f9f7] px-4 text-[13px] dark:bg-white/[0.06] dark:border-white/10"
              />
              <input
                value={seedForm.nombres}
                onChange={(e) =>
                  setSeedForm({ ...seedForm, nombres: e.target.value })
                }
                required
                placeholder="Nombres"
                className="h-11 rounded-full border border-black/10 bg-[#f9f9f7] px-4 text-[13px] dark:bg-white/[0.06] dark:border-white/10"
              />
              <input
                value={seedForm.apellidos}
                onChange={(e) =>
                  setSeedForm({ ...seedForm, apellidos: e.target.value })
                }
                required
                placeholder="Apellidos"
                className="col-span-2 h-11 rounded-full border border-black/10 bg-[#f9f9f7] px-4 text-[13px] dark:bg-white/[0.06] dark:border-white/10"
              />
              <input
                value={seedForm.correo_electronico}
                onChange={(e) =>
                  setSeedForm({
                    ...seedForm,
                    correo_electronico: e.target.value,
                  })
                }
                required
                placeholder="correo@empresa.com"
                className="col-span-2 h-11 rounded-full border border-black/10 bg-[#f9f9f7] px-4 text-[13px] dark:bg-white/[0.06] dark:border-white/10"
              />
              <input
                value={seedForm.contrasena}
                onChange={(e) =>
                  setSeedForm({ ...seedForm, contrasena: e.target.value })
                }
                required
                placeholder="Contraseña"
                className="col-span-2 h-11 rounded-full border border-black/10 bg-[#f9f9f7] px-4 text-[13px] dark:bg-white/[0.06] dark:border-white/10"
              />
            </div>
            <div className="mt-7 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setSeedModal(null)}
                className="h-10 rounded-full border border-black/10 px-5 text-[13px] dark:border-white/10"
              >
                Cancelar
              </button>
              <button className="h-10 rounded-full bg-black px-6 text-[13px] font-medium text-white dark:bg-white dark:text-black">
                Crear admin
              </button>
            </div>
          </form>
        </div>
      )}
    </TenancyLayout>
  );
}
